<template>
  <div>
    <AdminHeader />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Approval Workflows</h5>
        <p class="page-sub">
          <router-link :to="$url('/settings')" class="breadcrumb-link">Settings</router-link>
          <span class="breadcrumb-sep">/</span>
          <span>Approval Workflows</span>
        </p>
      </div>
    </div>

    <!-- Header card -->
    <div class="header-card">
      <div>
        <h2 class="header-card-title">Approval Workflows</h2>
        <p class="header-card-sub">
          Configure who approves requests for each module. Steps can be assigned by
          position, a specific employee, or automatically resolved to the submitter's
          Head of Department or Head of Unit.
        </p>
      </div>
      <button v-if="permission?.create_workflow == 1" class="btn-dark" @click="openCreate">
        <font-awesome-icon icon="plus" class="me-1" /> New Workflow
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <div class="search-wrap">
        <font-awesome-icon icon="search" class="search-icon" />
        <input type="text" class="search-input" placeholder="Search workflows..." v-model="search" />
      </div>
      <select class="filter-select" v-model="moduleFilter">
        <option value="all">All Modules</option>
        <option v-for="m in MODULES" :key="m.value" :value="m.value">{{ m.label }}</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-block">
      <div class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;" role="status"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="filtered.length === 0" class="empty-state">
      <div class="empty-icon-wrap"><font-awesome-icon icon="layer-group" /></div>
      <h3 class="empty-title">No workflows found</h3>
      <p class="empty-sub">Create your first approval workflow to define who approves payroll, leave, loans and more.</p>
      <button v-if="permission?.create_workflow == 1" class="btn-dark mt-4" @click="openCreate">
        <font-awesome-icon icon="plus" class="me-1" /> Create Workflow
      </button>
    </div>

    <!-- Workflow cards -->
    <div v-else class="workflow-list">
      <div v-for="wf in filtered" :key="wf.reference"
        class="workflow-card"
        :class="{ 'workflow-card--inactive': !wf.is_active }">

        <div class="wf-card-hd">
          <div class="wf-card-left">
            <div class="wf-icon" :class="wf.is_active ? 'wf-icon--dark' : 'wf-icon--gray'">
              <font-awesome-icon icon="sitemap" />
            </div>
            <div class="wf-info">
              <div class="wf-title-row">
                <span class="wf-name">{{ wf.name }}</span>
                <span class="status-badge" :class="wf.is_active ? 'status-badge--green' : 'status-badge--amber'">
                  {{ wf.is_active ? 'Active' : 'Inactive' }}
                </span>
                <span class="module-tag">{{ moduleLabel(wf.module) }}</span>
              </div>
              <p v-if="wf.description" class="wf-desc">{{ wf.description }}</p>
              <p class="wf-meta">
                {{ wf.steps.length }} step{{ wf.steps.length !== 1 ? 's' : '' }} ·
                {{ wf.is_sequential ? 'Sequential' : 'Parallel' }}
              </p>
            </div>
          </div>

          <div class="wf-actions">
            <button v-if="permission?.edit_workflow == 1" class="wf-action-btn"
              :class="wf.is_active ? 'wf-action-btn--green' : 'wf-action-btn--gray'"
              :disabled="togglingId === wf.reference"
              :title="wf.is_active ? 'Deactivate' : 'Activate'"
              @click="confirmToggle(wf)">
              <font-awesome-icon
                :icon="togglingId === wf.reference ? 'spinner' : (wf.is_active ? 'check-circle' : 'times-circle')"
                :class="{ 'fa-spin': togglingId === wf.reference }" />
            </button>
            <button v-if="permission?.edit_workflow == 1" class="wf-action-btn wf-action-btn--gray" @click="openEdit(wf)">
              <font-awesome-icon icon="pen" />
            </button>
            <button v-if="permission?.delete_workflow == 1" class="wf-action-btn wf-action-btn--red"
              :disabled="deletingId === wf.reference"
              @click="confirmDelete(wf)">
              <font-awesome-icon
                :icon="deletingId === wf.reference ? 'spinner' : 'trash'"
                :class="{ 'fa-spin': deletingId === wf.reference }" />
            </button>
            <button class="wf-action-btn wf-action-btn--gray" @click="toggleExpanded(wf.reference)">
              <font-awesome-icon :icon="expandedId === wf.reference ? 'chevron-up' : 'chevron-down'" />
            </button>
          </div>
        </div>

        <!-- Steps (expanded) -->
        <div v-if="expandedId === wf.reference" class="wf-steps">
          <p class="wf-steps-title">Approval Steps</p>
          <div class="wf-step-list">
            <div v-for="(step, i) in wf.steps" :key="step.id || i" class="wf-step-row">
              <div class="step-num">{{ step.step_order }}</div>
              <div class="step-info">
                <p class="step-name">{{ step.name }}</p>
                <p class="step-meta">
                  <span class="approver-type-badge" :class="'atype--' + (step.approver_type || 'position')">
                    {{ approverTypeLabel(step.approver_type) }}
                  </span>
                  <span v-if="step.approver_type === 'position' || !step.approver_type">
                    · <strong>{{ step.designation ? step.designation.name : step.position_id }}</strong>
                  </span>
                  <span v-else-if="step.approver_type === 'employee'">
                    · <strong>{{ step.approver_employee ? (step.approver_employee.first_name + ' ' + step.approver_employee.last_name) : '—' }}</strong>
                  </span>
                </p>
              </div>
              <div class="step-badges">
                <span v-if="step.is_required" class="badge-required">Required</span>
                <span v-if="step.timeout_hours" class="badge-timeout">{{ step.timeout_hours }}h timeout</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <!-- ══════════════════════════════
         WORKFLOW FORM MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: showForm }" @click.self="closeForm">
      <div class="modal-box modal-box--lg">
        <div class="modal-hd">
          <div>
            <p class="modal-ttl">{{ editingWorkflow ? 'Edit Workflow' : 'Create Approval Workflow' }}</p>
            <p class="modal-sub">Configure who approves at each step of the chain.</p>
          </div>
          <button class="modal-close-btn" @click="closeForm">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="modal-bd">
          <!-- Workflow fields -->
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label-sm">Workflow Name</label>
              <input type="text" class="field-input" placeholder="e.g. Payroll Approval Flow" v-model="form.name" />
            </div>
            <div class="field-group">
              <label class="field-label-sm">Module</label>
              <select class="field-input field-select" v-model="form.module" :disabled="!!editingWorkflow">
                <option
                  v-for="m in MODULES" :key="m.value" :value="m.value"
                  :disabled="!editingWorkflow && workflows.some(w => w.module === m.value)"
                >{{ m.label }}{{ !editingWorkflow && workflows.some(w => w.module === m.value) ? ' (already configured)' : '' }}</option>
              </select>
            </div>
            <div class="field-group field-group--full">
              <label class="field-label-sm">Description <span class="opt">(optional)</span></label>
              <textarea class="field-input" rows="2" placeholder="Briefly describe this workflow..." v-model="form.description"></textarea>
            </div>
            <div class="field-group field-group--full">
              <label class="sequential-label">
                <input type="checkbox" v-model="form.is_sequential" class="row-check" />
                <div>
                  <p class="sequential-title">Sequential Approval</p>
                  <p class="sequential-hint">Steps must be completed in order</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Steps -->
          <div class="steps-section">
            <div class="steps-section-hd">
              <span class="steps-section-title">Approval Steps</span>
              <button class="btn-dark btn-sm" @click="addStep">
                <font-awesome-icon icon="plus" class="me-1" /> Add Step
              </button>
            </div>

            <div class="step-form-list">
              <div v-for="(step, i) in formSteps" :key="i" class="step-form-card">
                <div class="step-form-hd">
                  <div class="step-form-num-row">
                    <div class="step-num">{{ step.step_order }}</div>
                    <span class="step-form-label">Step {{ step.step_order }}</span>
                  </div>
                  <button v-if="formSteps.length > 1" class="btn-step-remove" @click="removeStep(i)">
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>

                <div class="fields-grid">

                  <!-- Step name -->
                  <div class="field-group">
                    <label class="field-label-sm">Step Name</label>
                    <input type="text" class="field-input" placeholder="e.g. Line Manager Review" v-model="step.name" />
                  </div>

                  <!-- Approver type -->
                  <div class="field-group">
                    <label class="field-label-sm">Approver Type</label>
                    <div class="atype-row">
                      <button v-for="t in APPROVER_TYPES" :key="t.value"
                        type="button"
                        class="atype-btn"
                        :class="{ 'atype-btn--active': step.approver_type === t.value }"
                        @click="setApproverType(step, t.value)">
                        <font-awesome-icon :icon="t.icon" class="me-1" />{{ t.label }}
                      </button>
                    </div>
                  </div>

                  <!-- Position picker (type = position) -->
                  <div v-if="step.approver_type === 'position'" class="field-group field-group--full">
                    <label class="field-label-sm">Required Designation</label>
                    <select class="field-input field-select" v-model="step.position_id">
                      <option value="">Select designation...</option>
                      <option v-for="d in designations" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                    <p class="field-hint">Anyone holding this designation can approve this step.</p>
                  </div>

                  <!-- Employee picker (type = employee) -->
                  <div v-else-if="step.approver_type === 'employee'" class="field-group field-group--full">
                    <label class="field-label-sm">Specific Approver</label>
                    <search-select
                      v-model="step.employee_id"
                      url="payroll/employees/active"
                      query-param="search"
                      label-key="full_name"
                      sublabel-key="employee_no"
                      value-key="id"
                      response-key="data"
                      placeholder="Select employee..."
                      search-placeholder="Type name or number…"
                      :min-chars="1"
                    />
                    <p class="field-hint">This specific person will be required to approve, regardless of their position.</p>
                  </div>

                  <!-- HoD info (type = hod) -->
                  <div v-else-if="step.approver_type === 'hod'" class="field-group field-group--full">
                    <div class="atype-info atype-info--hod">
                      <font-awesome-icon icon="info-circle" class="atype-info-icon" />
                      <div>
                        <p class="atype-info-title">Head of Department (auto-resolved)</p>
                        <p class="atype-info-body">
                          When a request is submitted, the system will look up the submitting employee's department
                          and assign the Head of that Department as the approver for this step.
                          If the department has no head assigned, this step will be skipped.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- HoU info (type = hou) -->
                  <div v-else-if="step.approver_type === 'hou'" class="field-group field-group--full">
                    <div class="atype-info atype-info--hou">
                      <font-awesome-icon icon="info-circle" class="atype-info-icon" />
                      <div>
                        <p class="atype-info-title">Head of Unit (auto-resolved)</p>
                        <p class="atype-info-body">
                          When a request is submitted, the system will look up the submitting employee's unit
                          and assign the Head of that Unit as the approver for this step.
                          If the employee has no unit assigned, this step will be skipped.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Timeout + required -->
                  <div class="field-group">
                    <label class="field-label-sm">Timeout <span class="opt">(hours, optional)</span></label>
                    <input type="number" class="field-input" placeholder="e.g. 48" min="1"
                      :value="step.timeout_hours || ''"
                      @input="step.timeout_hours = $event.target.value ? Number($event.target.value) : null" />
                  </div>
                  <div class="field-group step-required-check">
                    <label class="radio-label">
                      <input type="checkbox" v-model="step.is_required" class="row-check" />
                      <span>Required step</span>
                    </label>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-ft">
          <button class="btn-ghost" @click="closeForm">Cancel</button>
          <button class="btn-dark" :disabled="saving" @click="handleSave">
            <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin me-1" />
            {{ editingWorkflow ? 'Save Changes' : 'Create Workflow' }}
          </button>
        </div>
      </div>
    </div>


    <!-- ══════════════════════════════
         CONFIRM MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: !!confirmConfig }" @click.self="closeConfirm">
      <div class="modal-box modal-box--sm">
        <div v-if="confirmConfig">
          <div class="confirm-icon-wrap">
            <div class="confirm-icon" :class="confirmConfig.danger ? 'confirm-icon--red' : 'confirm-icon--amber'">
              <font-awesome-icon :icon="confirmConfig.icon" />
            </div>
          </div>
          <div class="confirm-body">
            <p class="confirm-title">{{ confirmConfig.title }}</p>
            <p class="confirm-msg">{{ confirmConfig.message }}</p>
          </div>
          <div class="confirm-actions">
            <button class="confirm-btn confirm-btn--cancel" :disabled="confirmLoading" @click="closeConfirm">Cancel</button>
            <button class="confirm-btn"
              :class="confirmConfig.danger ? 'confirm-btn--red' : 'confirm-btn--dark'"
              :disabled="confirmLoading"
              @click="confirmConfig.onConfirm()">
              <font-awesome-icon v-if="confirmLoading" icon="spinner" class="fa-spin me-1" />
              {{ confirmConfig.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showForm || !!confirmConfig" class="modal-backdrop-custom" />
  </div>
</template>


<script>
import AdminHeader from "./administrationheadertab.vue";
import SearchSelect from "../../../components/SearchSelect.vue";
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

const MODULES = [
  { value: 'payroll',         label: 'Payroll' },
  { value: 'leave',           label: 'Leave' },
  { value: 'loan',            label: 'Loan' },
  { value: 'expense',         label: 'Expense' },
  { value: 'journal',         label: 'Journal Posting' },
  { value: 'purchase',             label: 'Purchase Order (Inventory)' },
  { value: 'purchase_requisition', label: 'Purchase Requisition' },
  { value: 'purchase_order',       label: 'Procurement Purchase Order' },
  { value: 'internal-issue',       label: 'Internal Stock Requisition' },
  { value: 'job-offer',       label: 'Recruitment - Job Offer' },
  { value: 'workforce-plan',  label: 'Recruitment - Workforce Plan' },
  { value: 'job-requisition', label: 'Recruitment - Job Requisition' },
  { value: 'job-post',        label: 'Recruitment - Job Post' },
  { value: 'vendor',          label: 'Vendor Registration' },
  { value: 'crm_campaign',   label: 'CRM Campaign' },
  { value: 'exit_case',      label: 'Exit / Resignation' },
];

const APPROVER_TYPES = [
  { value: 'position', label: 'Position',       icon: 'id-badge' },
  { value: 'employee', label: 'Specific Person', icon: 'user' },
  { value: 'hod',      label: 'Head of Dept',   icon: 'building' },
  { value: 'hou',      label: 'Head of Unit',   icon: 'sitemap' },
];

const emptyForm = () => ({
  name:          '',
  module:        'payroll',
  description:   '',
  is_sequential: true,
});

const emptyStep = (order = 1) => ({
  name:          '',
  step_order:    order,
  approver_type: 'position',
  position_id:   '',
  employee_id:   '',
  is_required:   true,
  timeout_hours: null,
});

export default {
  name: 'ApprovalWorkflowPage',

  components: { AdminHeader, SearchSelect },

  data() {
    return {
      MODULES,
      APPROVER_TYPES,

      loading:        false,
      saving:         false,
      confirmLoading: false,
      deletingId:     null,
      togglingId:     null,

      workflows:    [],
      designations: [],
      employees:    [],

      search:       '',
      moduleFilter: 'all',
      searchTimer:  null,
      expandedId:   null,

      showForm:        false,
      editingWorkflow: null,
      form:            emptyForm(),
      formSteps:       [emptyStep(1)],

      confirmConfig: null,

      toast: useToast(),
      store: useStore(),
    };
  },

  watch: {
    search()      { this.debouncedFetchWorkflows(); },
    moduleFilter(){ this.fetchWorkflows(); },
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    filtered() {
      return this.workflows;
    },
  },

  created() {
    this.fetchWorkflows();
    this.fetchDesignations();
    this.fetchEmployees();
  },

  methods: {
    debouncedFetchWorkflows() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchWorkflows(), 300);
    },

    fetchWorkflows() {
      this.loading = true;
      const params = {};
      if (this.moduleFilter !== 'all') params.module = this.moduleFilter;
      if (this.search.trim())          params.search = this.search.trim();
      axios.get('/approvals/workflows', { params })
        .then(({ data }) => {
          this.workflows = Array.isArray(data.data?.data) ? data.data.data
            : Array.isArray(data.data) ? data.data : [];
        })
        .catch(() => this.toast.error('Could not load workflows.'))
        .finally(() => { this.loading = false; });
    },

    fetchDesignations() {
      axios.get('/payroll/position/active')
        .then(({ data }) => {
          this.designations = Array.isArray(data.data?.data) ? data.data.data
            : Array.isArray(data.data) ? data.data : [];
        })
        .catch(() => {});
    },

    fetchEmployees() {
      axios.get('/payroll/employees/active')
        .then(({ data }) => {
          this.employees = Array.isArray(data.data?.data) ? data.data.data
            : Array.isArray(data.data) ? data.data : [];
        })
        .catch(() => {});
    },

    moduleLabel(val) {
      return MODULES.find(m => m.value === val)?.label ?? val;
    },

    approverTypeLabel(val) {
      return APPROVER_TYPES.find(t => t.value === val)?.label ?? 'Position';
    },

    toggleExpanded(ref) {
      this.expandedId = this.expandedId === ref ? null : ref;
    },

    setApproverType(step, type) {
      step.approver_type = type;
      // Clear fields that don't belong to the new type
      if (type !== 'position') step.position_id = '';
      if (type !== 'employee') step.employee_id = '';
    },

    // ── Form modal ─────────────────────────────────────────────────────────

    openCreate() {
      this.editingWorkflow = null;
      this.form      = emptyForm();
      this.formSteps = [emptyStep(1)];
      this.showForm  = true;
    },

    openEdit(wf) {
      this.editingWorkflow = wf;
      this.form = {
        name:          wf.name,
        module:        wf.module,
        description:   wf.description || '',
        is_sequential: wf.is_sequential,
      };
      this.formSteps = wf.steps?.length
        ? wf.steps.map(s => ({
            id:            s.id,
            name:          s.name,
            step_order:    s.step_order,
            approver_type: s.approver_type || 'position',
            position_id:   s.position_id || s.designation?.id || '',
            employee_id:   s.employee_id || s.approver_employee?.id || '',
            is_required:   s.is_required,
            timeout_hours: s.timeout_hours || null,
          }))
        : [emptyStep(1)];
      this.showForm = true;
    },

    closeForm() {
      this.showForm        = false;
      this.editingWorkflow = null;
    },

    addStep() {
      this.formSteps.push(emptyStep(this.formSteps.length + 1));
    },

    removeStep(index) {
      this.formSteps.splice(index, 1);
      this.formSteps.forEach((s, i) => { s.step_order = i + 1; });
    },

    validate() {
      if (!this.form.name.trim()) {
        this.toast.error('Workflow name is required.');
        return false;
      }
      if (!this.editingWorkflow) {
        const taken = this.workflows.some(w => w.module === this.form.module);
        if (taken) {
          this.toast.error(`A workflow for the '${this.moduleLabel(this.form.module)}' module already exists. Only one workflow per module is allowed.`);
          return false;
        }
      }
      for (const step of this.formSteps) {
        if (!step.name.trim()) {
          this.toast.error('All step names are required.');
          return false;
        }
        if (step.approver_type === 'position' && !step.position_id) {
          this.toast.error(`Step "${step.name || step.step_order}": a designation is required for Position type.`);
          return false;
        }
        if (step.approver_type === 'employee' && !step.employee_id) {
          this.toast.error(`Step "${step.name || step.step_order}": a specific employee must be selected.`);
          return false;
        }
      }
      return true;
    },

    handleSave() {
      if (!this.validate()) return;

      const payload = {
        ...this.form,
        steps: this.formSteps.map(s => ({
          id:            s.id,
          name:          s.name,
          step_order:    s.step_order,
          approver_type: s.approver_type,
          position_id:   s.approver_type === 'position' ? s.position_id : null,
          employee_id:   s.approver_type === 'employee' ? s.employee_id : null,
          is_required:   s.is_required,
          timeout_hours: s.timeout_hours || null,
        })),
      };

      this.saving = true;

      if (this.editingWorkflow) {
        axios.put(`/approvals/workflows/${this.editingWorkflow.id}`, payload)
          .then(() => {
            this.toast.success('Workflow updated successfully.');
            this.closeForm();
            this.fetchWorkflows();
          })
          .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to update workflow.'))
          .finally(() => { this.saving = false; });
      } else {
        axios.post('/approvals/workflows', payload)
          .then(() => {
            this.toast.success('Workflow created successfully.');
            this.closeForm();
            this.fetchWorkflows();
          })
          .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to create workflow.'))
          .finally(() => { this.saving = false; });
      }
    },

    // ── Confirm modal ──────────────────────────────────────────────────────

    closeConfirm() {
      if (!this.confirmLoading) this.confirmConfig = null;
    },

    confirmDelete(wf) {
      this.confirmConfig = {
        title:   'Delete Workflow',
        message: `Are you sure you want to delete "${wf.name}"? This cannot be undone.`,
        label:   'Delete Workflow',
        icon:    'trash',
        danger:  true,
        onConfirm: () => {
          this.confirmLoading = true;
          this.deletingId     = wf.reference;
          axios.delete(`/approvals/workflows/${wf.id}`)
            .then(() => {
              this.toast.success('Workflow deleted.');
              this.fetchWorkflows();
            })
            .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to delete workflow.'))
            .finally(() => {
              this.confirmLoading = false;
              this.deletingId     = null;
              this.confirmConfig  = null;
            });
        },
      };
    },

    confirmToggle(wf) {
      const activating = !wf.is_active;
      this.confirmConfig = {
        title:   activating ? 'Activate Workflow' : 'Deactivate Workflow',
        message: activating
          ? `Activating "${wf.name}" will make it the live approval chain for ${wf.module}.`
          : `Deactivating "${wf.name}" means no approval workflow will run for ${wf.module}.`,
        label:   activating ? 'Yes, Activate' : 'Yes, Deactivate',
        icon:    activating ? 'check-circle' : 'exclamation-triangle',
        danger:  false,
        onConfirm: () => {
          this.confirmLoading = true;
          this.togglingId     = wf.reference;
          axios.put(`/approvals/workflows/status/${wf.id}`)
            .then(() => {
              this.toast.success(`Workflow ${activating ? 'activated' : 'deactivated'}.`);
              this.fetchWorkflows();
            })
            .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to update workflow status.'))
            .finally(() => {
              this.confirmLoading = false;
              this.togglingId     = null;
              this.confirmConfig  = null;
            });
        },
      };
    },
  },
};
</script>


<style scoped>
/* ── utilities ── */
.me-1 { margin-right: 4px; }
.mt-4 { margin-top: 16px; }
.opt  { font-size: 11px; color: #94a3b8; font-weight: 400; }

/* ── page ── */
.page-header  { margin-bottom: 20px; }
.page-title   { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 4px; }
.page-sub     { font-size: 13px; color: #7a90b0; margin: 0; display: flex; align-items: center; gap: 6px; }
.breadcrumb-link { color: #7a90b0; text-decoration: none; }
.breadcrumb-link:hover { color: #1a2b45; }
.breadcrumb-sep { color: #c5d0e0; }

/* ── header card ── */
.header-card       { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; padding: 20px 24px; margin-bottom: 20px; display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 14px; }
.header-card-title { font-size: 18px; font-weight: 700; color: #1a2b45; margin: 0 0 5px; }
.header-card-sub   { font-size: 13px; color: #7a90b0; margin: 0; max-width: 560px; line-height: 1.6; }

/* ── buttons ── */
.btn-dark       { display: inline-flex; align-items: center; gap: 6px; background: #1a2b45; color: #fff; font-size: 13px; font-weight: 600; padding: 9px 18px; border-radius: 9px; border: none; cursor: pointer; transition: background .15s; white-space: nowrap; font-family: inherit; }
.btn-dark:hover:not(:disabled) { background: #0f1e33; }
.btn-dark:disabled { opacity: .5; cursor: not-allowed; }
.btn-sm         { padding: 6px 12px; font-size: 12px; }
.btn-ghost      { display: inline-flex; align-items: center; gap: 6px; background: #f4f7fb; color: #3d5068; font-size: 13px; font-weight: 600; padding: 9px 18px; border-radius: 9px; border: none; cursor: pointer; font-family: inherit; }
.btn-ghost:hover { background: #e4eaf2; }

/* ── filters ── */
.filters-row  { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 18px; }
.search-wrap  { position: relative; display: flex; align-items: center; flex: 1; min-width: 200px; max-width: 360px; }
.search-icon  { position: absolute; left: 11px; color: #7a90b0; font-size: 13px; pointer-events: none; }
.search-input { width: 100%; padding: 8px 12px 8px 34px; border: 1px solid #dde5f0; border-radius: 9px; font-size: 13px; color: #1a2b45; background: #fff; outline: none; }
.search-input:focus { border-color: #1a2b45; }
.filter-select { padding: 8px 12px; border: 1px solid #dde5f0; border-radius: 9px; font-size: 13px; color: #1a2b45; background: #fff; outline: none; cursor: pointer; }

/* ── states ── */
.state-block { display: flex; flex-direction: column; align-items: center; padding: 60px 24px; }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 60px 24px; text-align: center; background: #fff; border: 2px dashed #e4eaf2; border-radius: 14px; }
.empty-icon-wrap { width: 64px; height: 64px; border-radius: 50%; background: #f0f4f8; display: flex; align-items: center; justify-content: center; font-size: 26px; color: #94a3b8; margin-bottom: 14px; }
.empty-title { font-size: 16px; font-weight: 700; color: #1a2b45; margin: 0 0 6px; }
.empty-sub   { font-size: 13px; color: #7a90b0; margin: 0; max-width: 320px; line-height: 1.6; }

/* ── workflow list ── */
.workflow-list     { display: flex; flex-direction: column; gap: 14px; }
.workflow-card     { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; overflow: hidden; }
.workflow-card--inactive { opacity: .7; }

.wf-card-hd   { display: flex; align-items: flex-start; justify-content: space-between; padding: 18px 20px; gap: 12px; }
.wf-card-left { display: flex; align-items: flex-start; gap: 12px; min-width: 0; flex: 1; }
.wf-icon      { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 15px; color: #fff; flex-shrink: 0; }
.wf-icon--dark { background: #1a2b45; }
.wf-icon--gray { background: #94a3b8; }
.wf-info      { min-width: 0; flex: 1; }
.wf-title-row { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; margin-bottom: 3px; }
.wf-name      { font-size: 14px; font-weight: 700; color: #1a2b45; }
.wf-desc      { font-size: 12px; color: #7a90b0; margin: 0 0 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 480px; }
.wf-meta      { font-size: 11.5px; color: #94a3b8; margin: 0; }

.status-badge        { display: inline-block; font-size: 11px; font-weight: 700; border-radius: 20px; padding: 2px 9px; }
.status-badge--green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
.status-badge--amber { background: #fefce8; color: #ca8a04; border: 1px solid #fde68a; }
.module-tag          { background: #f0f4f8; color: #64748b; font-size: 11.5px; font-weight: 600; border-radius: 20px; padding: 2px 9px; }

.wf-actions     { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.wf-action-btn  { width: 32px; height: 32px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center; font-size: 13px; cursor: pointer; background: transparent; }
.wf-action-btn:disabled { opacity: .5; cursor: not-allowed; }
.wf-action-btn--green { color: #16a34a; }
.wf-action-btn--green:hover { background: #f0fdf4; }
.wf-action-btn--gray  { color: #7a90b0; }
.wf-action-btn--gray:hover { background: #f4f7fb; }
.wf-action-btn--red   { color: #e03535; }
.wf-action-btn--red:hover { background: #fff0f0; }

/* ── expanded steps ── */
.wf-steps        { border-top: 1px solid #f0f4f8; padding: 16px 20px 20px; }
.wf-steps-title  { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; margin: 0 0 10px; }
.wf-step-list    { display: flex; flex-direction: column; gap: 8px; }
.wf-step-row     { display: flex; align-items: center; gap: 12px; border: 1px solid #f0f4f8; border-radius: 9px; background: #f8fafc; padding: 10px 14px; }
.step-num        { width: 28px; height: 28px; border-radius: 50%; background: #1a2b45; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.step-info       { flex: 1; min-width: 0; }
.step-name       { font-size: 13px; font-weight: 600; color: #1a2b45; margin: 0 0 3px; }
.step-meta       { font-size: 12px; color: #7a90b0; margin: 0; display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.step-badges     { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.badge-required  { background: #1a2b45; color: #fff; font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 20px; }
.badge-timeout   { font-size: 10px; color: #94a3b8; }

/* approver type badge in list */
.approver-type-badge { display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 700; border-radius: 5px; padding: 1px 7px; }
.atype--position { background: #eff6ff; color: #1d4ed8; }
.atype--employee { background: #faf5ff; color: #7c3aed; }
.atype--hod      { background: #fefce8; color: #a16207; }
.atype--hou      { background: #f0fdf4; color: #15803d; }

/* ── form fields ── */
.fields-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field-group     { display: flex; flex-direction: column; gap: 4px; }
.field-group--full { grid-column: 1 / -1; }
.field-label-sm  { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #7a90b0; }
.field-hint      { font-size: 10.5px; color: #94a3b8; margin: 2px 0 0; }
.field-input     { padding: 9px 12px; border: 1px solid #dde5f0; border-radius: 9px; font-size: 13px; color: #1a2b45; background: #fff; outline: none; width: 100%; font-family: inherit; }
.field-input:focus { border-color: #1a2b45; box-shadow: 0 0 0 3px rgba(26,43,69,.07); }
.field-select    { appearance: auto; cursor: pointer; }
textarea.field-input { resize: vertical; }
.row-check       { width: 15px; height: 15px; cursor: pointer; accent-color: #1a2b45; }
.sequential-label  { display: flex; align-items: flex-start; gap: 10px; border: 1px solid #e4eaf2; border-radius: 9px; padding: 12px 14px; cursor: pointer; }
.sequential-title  { font-size: 13px; font-weight: 600; color: #1a2b45; margin: 0 0 2px; }
.sequential-hint   { font-size: 12px; color: #7a90b0; margin: 0; }

/* ── approver type toggle ── */
.atype-row      { display: flex; gap: 5px; flex-wrap: wrap; }
.atype-btn      { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; padding: 5px 10px; border-radius: 7px; border: 1px solid #dde5f0; background: #f8fafc; color: #64748b; cursor: pointer; transition: all .15s; font-family: inherit; }
.atype-btn:hover { border-color: #1a2b45; color: #1a2b45; }
.atype-btn--active { background: #1a2b45; color: #fff; border-color: #1a2b45; }

/* ── approver type info boxes ── */
.atype-info        { display: flex; align-items: flex-start; gap: 10px; border-radius: 9px; padding: 12px 14px; border: 1px solid; }
.atype-info--hod   { background: #fefce8; border-color: #fde68a; }
.atype-info--hou   { background: #f0fdf4; border-color: #bbf7d0; }
.atype-info-icon   { font-size: 15px; flex-shrink: 0; margin-top: 1px; }
.atype-info--hod .atype-info-icon { color: #a16207; }
.atype-info--hou .atype-info-icon { color: #15803d; }
.atype-info-title  { font-size: 12.5px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
.atype-info-body   { font-size: 12px; color: #475569; margin: 0; line-height: 1.6; }

/* ── step form ── */
.steps-section    { margin-top: 4px; }
.steps-section-hd { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.steps-section-title { font-size: 13.5px; font-weight: 700; color: #1a2b45; }
.step-form-list   { display: flex; flex-direction: column; gap: 12px; }
.step-form-card   { background: #f8fafc; border: 1px solid #e4eaf2; border-radius: 10px; padding: 14px 16px; }
.step-form-hd     { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.step-form-num-row { display: flex; align-items: center; gap: 8px; }
.step-form-label  { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #7a90b0; }
.btn-step-remove  { width: 28px; height: 28px; border-radius: 6px; border: 1px solid #fecaca; background: #fff0f0; color: #e03535; display: flex; align-items: center; justify-content: center; font-size: 11px; cursor: pointer; }
.btn-step-remove:hover { background: #e03535; color: #fff; }
.step-required-check { justify-content: flex-end; }
.radio-label      { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #3d5068; cursor: pointer; }

/* ── modals ── */
.modal-overlay       { position: fixed; inset: 0; z-index: 1050; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.modal-overlay.open  { opacity: 1; pointer-events: all; }
.modal-backdrop-custom { position: fixed; inset: 0; z-index: 1040; background: rgba(15,25,50,.5); backdrop-filter: blur(2px); }
.modal-box           { background: #fff; border-radius: 14px; width: 100%; max-width: 480px; margin: 16px; box-shadow: 0 20px 60px rgba(0,0,0,.2); overflow: hidden; transform: translateY(14px); transition: transform .22s ease; z-index: 1051; position: relative; max-height: 90vh; display: flex; flex-direction: column; }
.modal-box--lg       { max-width: 700px; }
.modal-box--sm       { max-width: 400px; }
.modal-overlay.open .modal-box { transform: translateY(0); }
.modal-hd            { padding: 18px 24px; border-bottom: 1px solid #e4eaf2; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-shrink: 0; }
.modal-ttl           { font-size: 15px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
.modal-sub           { font-size: 12px; color: #7a90b0; margin: 0; }
.modal-close-btn     { width: 30px; height: 30px; border-radius: 7px; border: 1px solid #dde5f0; background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #7a90b0; flex-shrink: 0; }
.modal-close-btn:hover { background: #f4f7fb; }
.modal-bd            { padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; overflow-y: auto; flex: 1; }
.modal-ft            { padding: 16px 24px; border-top: 1px solid #e4eaf2; display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }

/* ── confirm modal ── */
.confirm-icon-wrap  { display: flex; justify-content: center; padding: 28px 24px 8px; }
.confirm-icon       { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 22px; }
.confirm-icon--red  { background: #fff0f0; color: #e03535; }
.confirm-icon--amber { background: #fefce8; color: #ca8a04; }
.confirm-body       { padding: 10px 24px 16px; text-align: center; }
.confirm-title      { font-size: 16px; font-weight: 700; color: #1a2b45; margin: 0 0 8px; }
.confirm-msg        { font-size: 13px; color: #7a90b0; margin: 0; line-height: 1.6; }
.confirm-actions    { display: flex; gap: 10px; padding: 14px 20px 20px; }
.confirm-btn        { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-size: 13px; font-weight: 600; padding: 10px; border-radius: 9px; border: none; cursor: pointer; font-family: inherit; }
.confirm-btn:disabled { opacity: .55; cursor: not-allowed; }
.confirm-btn--cancel { background: #f4f7fb; color: #1a2b45; border: 1px solid #dde5f0; }
.confirm-btn--cancel:hover:not(:disabled) { background: #e4eaf2; }
.confirm-btn--red    { background: #e03535; color: #fff; }
.confirm-btn--red:hover:not(:disabled) { background: #c02828; }
.confirm-btn--dark   { background: #1a2b45; color: #fff; }
.confirm-btn--dark:hover:not(:disabled) { background: #0f1e33; }

@media (max-width: 640px) {
  .fields-grid { grid-template-columns: 1fr; }
  .field-group--full { grid-column: 1; }
  .header-card { flex-direction: column; }
  .atype-row { gap: 4px; }
}
</style>
